import { useState } from "react";
import { Eye, Send, Lock, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLoad, act } from "@/lib/hooks";
import { api, fdatetime } from "@/lib/api";
import { downloadWith } from "@/lib/portalApi";
import { useAccess } from "@/context/AuthContext";
import { Section, StatusBadge } from "@/components/common";
import { FileUploadButton, fsize } from "@/components/FileUploadButton";
import { ArtworkList } from "@/pages/portal/PortalJob";

const TONE = { approved: "paid", rejected: "failed", sent: "sent", superseded: "canceled" };
const STAFF = ["tenant_admin", "accountant", "sales", "production"];
const removeArt = async (a, reload) => { if (window.confirm(`Delete ${a.filename}? This frees its storage and cannot be undone.`) && await act(() => api.delete(`/artwork/${a.id}`), "Artwork deleted")) reload(); };

function ProofItem({ p, can, onStep }) {
  return (
    <li className="space-y-1 px-5 py-3 text-sm" data-testid={`proof-row-v${p.version}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs font-semibold">v{p.version}</span>
        <StatusBadge status={TONE[p.status] || "pending"} label={p.status_label} testId={`proof-status-v${p.version}`} />
        {p.locked && <Lock className="h-3.5 w-3.5 text-slate-500" />}
        <span className="min-w-0 flex-1 truncate text-slate-600">{p.filename || "file"} · {fsize(p.size || 0)}</span>
        <Button size="sm" variant="ghost" data-testid={`proof-download-v${p.version}`} onClick={() => downloadWith(api, `/proofs/${p.id}/file`, p.filename)}><Download className="h-4 w-4" /></Button>
        {can && p.status === "uploaded" && <Button size="sm" variant="outline" data-testid={`proof-review-v${p.version}`} onClick={() => onStep(p, "review")}><Eye className="mr-1 h-4 w-4" />Start review</Button>}
        {can && p.status === "in_review" && <Button size="sm" data-testid={`proof-send-v${p.version}`} onClick={() => onStep(p, "send")} className="bg-[#13294B]"><Send className="mr-1 h-4 w-4" />Send to client</Button>}
      </div>
      <div className="text-xs text-slate-500">by {p.uploaded_by || "—"} · {p.created_at ? fdatetime(p.created_at) : "—"}{p.decided_by && ` · ${p.status} by ${p.decided_by} ${fdatetime(p.decided_at)}`}</div>
      {p.customer_comment && <div className="text-xs text-rose-700" data-testid={`proof-comment-v${p.version}`}>Customer: {p.customer_comment}</div>}
    </li>
  );
}

function ProofingDialog({ jobId, to, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const save = async () => { if (await act(() => api.post(`/jobs/${jobId}/proofing`, { required: to, reason }), to ? "Proofing required for this job" : "Proofing set to not needed")) { onClose(); onDone(); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="proofing-dialog">
        <DialogHeader><DialogTitle>{to ? "Require proofing for this job" : "Proofing not needed for this job"}</DialogTitle>
          <DialogDescription>{to ? "Steps after file prep will wait for an approved customer proof. Steps already done stay as they are." : "This job will no longer wait for a customer proof. No step or status is moved."}</DialogDescription></DialogHeader>
        <Input data-testid="proofing-reason-input" placeholder="Reason (saved in the job history)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="proofing-confirm-button" disabled={reason.trim().length < 3} onClick={save} className="bg-[#13294B]">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProofingLog({ log }) {
  if (!log?.length) return null;
  const e = log[log.length - 1];
  return <li className="px-5 py-2 text-xs text-slate-500" data-testid="proofing-last-change">{e.required ? "Proofing required" : "Proofing set to not needed"} by {e.by} · {fdatetime(e.at)} · “{e.reason}”</li>;
}

export function JobProofPanel({ jobId, onChanged }) {
  const [d, reload] = useLoad(`/jobs/${jobId}/proofs`);
  const [to, setTo] = useState(null);
  const { canWrite } = useAccess();
  const can = canWrite(STAFF);
  const canToggle = canWrite(["tenant_admin", "production"]);
  if (!d) return null;
  const proofs = d.proofs || [];
  const need = d.proof_required;
  const open = !["dispatched", "closed", "cancelled"].includes(d.job_status);
  const locked = d.proof_status === "approved" || d.proof_status === "sent";
  const addProof = async (upload_id) => { if (await act(() => api.post(`/jobs/${jobId}/proofs`, { upload_id }), "Proof uploaded")) reload(); };
  const addArt = async (upload_id) => { if (await act(() => api.post("/artwork", { upload_id, target_type: "job", target_id: jobId }), "Artwork uploaded")) reload(); };
  const step = async (p, s) => { if (await act(() => api.post(`/proofs/${p.id}/${s}`, {}), s === "send" ? "Proof sent to client" : "Proof in review")) reload(); };
  return (
    <div className="grid gap-6 lg:grid-cols-2 print:hidden">
      <Section title={<span>Proofs {d.proof_status && <StatusBadge status={TONE[d.proof_status] || "pending"} label={proofs[0]?.status_label} testId="job-proof-status" className="ml-2" />}</span>} testId="job-proofs-section"
        action={<div className="flex flex-wrap justify-end gap-2">
          {canToggle && open && need && d.proof_status !== "approved" && <Button size="sm" variant="outline" data-testid="proofing-not-needed-button" onClick={() => setTo(false)}>Not needed</Button>}
          {can && !locked && <FileUploadButton client={api} base="/files" onUploaded={addProof} label={proofs.length ? "Upload revised proof" : "Upload proof"} testId="proof-upload-button" />}</div>}>
        <ul className="divide-y divide-slate-100">
          {!need && <li className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 text-sm text-slate-600" data-testid="proofing-not-needed-note"><span>Proofing not needed for this job</span>
            {canToggle && open && <Button size="sm" variant="outline" data-testid="proofing-require-button" onClick={() => setTo(true)}>Require proofing</Button>}</li>}
          {need && proofs.length === 0 && <li className="px-5 py-4 text-sm text-slate-400" data-testid="proofing-needed-note">No proofs yet. Production past Prepress needs an approved proof.</li>}
          {proofs.map((p) => <ProofItem key={p.id} p={p} can={can} onStep={step} />)}
          <ProofingLog log={d.proofing_log} />
        </ul>
      </Section>
      {to !== null && <ProofingDialog jobId={jobId} to={to} onClose={() => setTo(null)} onDone={() => { reload(); onChanged?.(); }} />}
      <Section title="Customer artwork" testId="job-artwork-section" action={can && <FileUploadButton client={api} base="/files" onUploaded={addArt} label="Add artwork" testId="staff-artwork-upload" />}>
        <ArtworkList rows={d.artwork || []} client={api} base="" testId="job-artwork-list" onDelete={can ? (a) => removeArt(a, reload) : undefined} />
      </Section>
    </div>
  );
}

export function QuoteArtworkPanel({ quoteId }) {
  const [rows, reload] = useLoad(`/artwork?target_type=quote&target_id=${quoteId}`);
  const { canWrite } = useAccess();
  const add = async (upload_id) => { if (await act(() => api.post("/artwork", { upload_id, target_type: "quote", target_id: quoteId }), "Artwork uploaded")) reload(); };
  return (
    <Section title="Customer artwork" testId="quote-artwork-section" action={canWrite(STAFF) && <FileUploadButton client={api} base="/files" onUploaded={add} label="Add artwork" testId="quote-artwork-upload" />}>
      <ArtworkList rows={rows || []} client={api} base="" testId="quote-artwork-list" onDelete={canWrite(STAFF) ? (a) => removeArt(a, reload) : undefined} />
    </Section>
  );
}
